import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import { QRCodeCanvas } from 'qrcode.react';
import {
  getMyProfile,
  updateMyProfile,
} from '../services/api';

const Profile = () => {
  const { user } = useAuth();
const [profileComplete, setProfileComplete] = useState(false);
  // =====================================================
  // Form state
  // =====================================================

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const [saved, setSaved] = useState(false);

  // =====================================================
  // Get logged-in user's profile
  // =====================================================

  const {
    data: profile,
    isLoading,
    isError,
  } = useApiQuery(
    ['user-profile'],
    () => getMyProfile().then((res) => res.data),
    {
      enabled: !!user?._id,
      staleTime: 5 * 60 * 1000,
    }
  );

  // =====================================================
  // Use fresh profile from backend
  // Fallback to AuthContext user
  // =====================================================

  const displayUser = profile || user;
const isProfileComplete =
  !!displayUser?.name?.trim() &&
  !!displayUser?.phone?.trim();
  // =====================================================
  // Populate editable fields when profile loads
  // =====================================================

 useEffect(() => {
  if (displayUser) {
    setName(displayUser.name || '');
    setPhone(displayUser.phone || '');

    setProfileComplete(
      !!displayUser.name?.trim() &&
      !!displayUser.phone?.trim()
    );
  }
}, [displayUser]);

  // =====================================================
  // Update profile mutation
  // =====================================================

  const updateProfileMutation = useApiMutation(
    (data) =>
      updateMyProfile(data).then((res) => res.data),
    {
      onSuccess: (data) => {
  setName(data.user.name || '');
  setPhone(data.user.phone || '');

  // Show QR immediately after successful save
  setProfileComplete(
    !!data.user.name?.trim() &&
    !!data.user.phone?.trim()
  );

  setSaved(true);

  setTimeout(() => {
    setSaved(false);
  }, 3000);
},
    }
  );

  // =====================================================
  // Formatted account information
  // =====================================================

  const formattedData = useMemo(() => {
    if (!displayUser) return null;

    return {
      email: displayUser.email || 'N/A',

      role: displayUser.role
        ? displayUser.role.replace(/_/g, ' ')
        : 'N/A',

      status: displayUser.status || 'inactive',

      isActive: displayUser.status === 'active',

      joinedDate: displayUser.createdAt
        ? new Date(displayUser.createdAt).toLocaleDateString(
            'en-IN',
            {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }
          )
        : 'N/A',
    };
  }, [displayUser]);

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>

          <p className="text-gray-500 text-sm">
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (isError) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Failed to load profile. Please refresh the page.
        </div>
      </div>
    );
  }

  // =====================================================
  // No user
  // =====================================================

  if (!displayUser) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
          Please log in to view your profile.
        </div>
      </div>
    );
  }

  const {
    email,
    role,
    status,
    isActive,
    joinedDate,
  } = formattedData;

  // =====================================================
  // QR DATA
  //
  // This never changes because it uses the permanent
  // MongoDB User._id.
  // =====================================================

  const qrData = JSON.stringify({
    type: 'attendee',
    userId: displayUser._id,
  });

  // =====================================================
  // Save profile
  // =====================================================

  const handleSaveProfile = (e) => {
    e.preventDefault();

    setSaved(false);

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName) {
      return;
    }

    if (!trimmedPhone) {
      return;
    }

    updateProfileMutation.mutate({
      name: trimmedName,
      phone: trimmedPhone,
    });
  };

  // =====================================================
  // Download QR
  // =====================================================

  const handleDownloadQR = () => {
    const canvas = document.getElementById('attendee-qr');

    if (!canvas) {
      return;
    }

    const pngUrl = canvas.toDataURL('image/png');

    const downloadLink = document.createElement('a');

    downloadLink.href = pngUrl;
    downloadLink.download = `${name || 'attendee'}-qr.png`;

    document.body.appendChild(downloadLink);

    downloadLink.click();

    document.body.removeChild(downloadLink);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">

      {/* =================================================
          PAGE TITLE
      ================================================= */}

      <h1 className="text-2xl font-bold mb-6 text-[#2b3445]">
        My Profile
      </h1>

      {/* =================================================
          PROFILE CARD
      ================================================= */}

      <div className="bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="flex items-center gap-4 pb-5 border-b border-[#dae1e7]">

          {/* Avatar */}

          <div className="w-16 h-16 rounded-full bg-[#1672b9]/10 flex items-center justify-center text-[#1672b9] text-2xl font-bold">

            {name
              ? name.charAt(0).toUpperCase()
              : '?'}
          </div>

          <div>

            <p className="text-lg font-semibold text-gray-900">
              {name || 'N/A'}
            </p>

            <p className="text-sm text-gray-500 capitalize">
              {role}
            </p>

          </div>

        </div>

        {/* =================================================
            PROFILE FORM
        ================================================= */}

        <form
          onSubmit={handleSaveProfile}
          className="mt-6 space-y-5"
        >

          {/* =================================================
              NAME
          ================================================= */}

          <div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
              disabled={updateProfileMutation.isPending}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all disabled:bg-gray-50"
            />

          </div>

          {/* =================================================
              EMAIL - LOCKED
          ================================================= */}

          <div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>

            <div className="relative">

              <input
                type="email"
                value={email}
                disabled
                className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 pr-10 text-sm text-gray-500 cursor-not-allowed"
              />

              {/* Lock icon */}

              

            </div>

            <p className="text-xs text-gray-400 mt-1">
              Email address cannot be changed.
            </p>

          </div>

          {/* =================================================
              PHONE
          ================================================= */}

          <div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>

            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              required
              disabled={updateProfileMutation.isPending}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all disabled:bg-gray-50"
            />

          </div>

          {/* =================================================
              SAVE BUTTON
          ================================================= */}

          <button
            type="submit"
            disabled={
              updateProfileMutation.isPending ||
              !name.trim() ||
              !phone.trim()
            }
            className="w-full bg-[#1672b9] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >

            {updateProfileMutation.isPending
              ? 'Saving...'
              : 'Save Profile'}

          </button>

          {/* =================================================
              SUCCESS MESSAGE
          ================================================= */}

          {saved && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              Profile updated successfully.
            </div>
          )}

          {/* =================================================
              ERROR MESSAGE
          ================================================= */}

          {updateProfileMutation.isError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">

              {updateProfileMutation.error?.response?.data?.message ||
                'Failed to update profile.'}

            </div>
          )}

        </form>

      </div>

      {/* =================================================
          QR CARD
      ================================================= */}

   {/* =================================================
    QR CARD
================================================= */}

<div className="mt-6 bg-white rounded-xl border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-6 text-center">

  <h2 className="text-lg font-semibold text-gray-900">
    Your Attendee QR
  </h2>

  <p className="text-sm text-gray-500 mt-1 mb-5">
    {isProfileComplete
      ? 'This QR code is permanently linked to your attendee account.'
      : 'Complete your profile to generate your attendee QR code.'}
  </p>

 {profileComplete ? (
    <div className="flex flex-col items-center">

      <div className="p-4 border border-gray-200 rounded-xl bg-white">
        <QRCodeCanvas
          id="attendee-qr"
          value={qrData}
          size={220}
          level="H"
          includeMargin
        />
      </div>

      <p className="mt-4 text-sm font-semibold text-gray-900">
        {name}
      </p>

      <p className="text-sm text-gray-500">
        {email}
      </p>

      {phone && (
        <p className="text-sm text-gray-500">
          {phone}
        </p>
      )}

      <button
        type="button"
        onClick={handleDownloadQR}
        className="mt-5 px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition"
      >
        Download QR
      </button>

    </div>
  ) : (
    <div className="py-8">

      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
        <span className="text-2xl">QR</span>
      </div>

      <p className="text-sm text-gray-500">
        Please enter your name and phone number,
        then save your profile.
      </p>

    </div>
  )}

</div>

      {/* =================================================
          ACCOUNT INFORMATION
      ================================================= */}

      <div className="mt-6 bg-white rounded-xl border border-[#dae1e7] p-6">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Account status */}

          <div>

            <p className="text-sm text-gray-500 font-medium">
              Account Status
            </p>

            <p
              className={`text-base font-medium capitalize ${
                isActive
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {status}
            </p>

          </div>

          {/* Member since */}

          <div>

            <p className="text-sm text-gray-500 font-medium">
              Member Since
            </p>

            <p className="text-base text-gray-900">
              {joinedDate}
            </p>

          </div>

        </div>

      </div>

    </div>
  );
};

export default Profile;